import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { IUser } from "./useUsers";
import { IWithdraw } from "./useCommunityWithdraws";
import { useAppLoading } from "./useAppLoading";

export interface IPaginatedDonationsResponse {
  data: IWithdraw[];
  totalAmount: number;
  totalCount: number;
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface IStore {
  allDonations: IPaginatedDonationsResponse | null;
  userDonations: IWithdraw[];
  donatorLeaderboard: IUser[];
  hasNextUserDonations: boolean;
  hasNextPage: boolean;
  getAllDonations: (page: number, limit: number, lotteryId?: string) => void;
  getDonationLeaderboard: () => void;
  getUserDonations: (userId: string, page: number, limit: number) => void;
  clearUserDonations: () => void;
  downloadDonationsInCSV: () => void;
}

export const useDonations = create<IStore>()((set) => ({
  allDonations: null,
  donatorLeaderboard: [],
  userDonations: [],
  hasNextUserDonations: true,
  hasNextPage: false,
  getAllDonations: async (page, limit, lotteryId) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (lotteryId) {
        params.append("lotteryId", lotteryId);
      }

      const response = await axios.get(
        `/withdraws/user-donations?${params.toString()}`
      );
      if (response?.data) {
        set((state) => ({
          hasNextPage: response.data.data.length >= limit,
          allDonations:
            page === 1
              ? response.data
              : {
                  ...response.data,
                  data: [
                    ...(state.allDonations?.data || []),
                    ...response.data.data,
                  ],
                },
        }));
      }
    } catch (error) {
      LOG.error("Error on fetching all donations", error);
    }
  },
  getDonationLeaderboard: async () => {
    try {
      const response = await axios.get("/users/donation-leaderboard");
      if (response?.data) {
        set({ donatorLeaderboard: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching donation leaderboard", error);
    }
  },
  getUserDonations: async (userId, page, limit) => {
    try {
      const response = await axios.get(
        `/withdraws/user-donations/${userId}?page=${page}&limit=${limit}`
      );
      if (response?.data) {
        set((state) => ({
          hasNextUserDonations: response?.data?.length === limit,
          userDonations:
            page === 1
              ? response?.data
              : [...state.userDonations, ...response?.data],
        }));
      }
    } catch (error) {
      LOG.error("Error on fetching user donations", error);
    }
  },
  downloadDonationsInCSV: async () => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`/withdraws/donations/csv`);
      if (response?.data) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "donations.csv");
        document.body.appendChild(link);
        link.click();
      }
    } catch (error) {
      LOG.error("Error on downloading Donations in CSV", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearUserDonations: () => {
    set({ userDonations: [] });
  },
}));
